import { useState, type ReactNode } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import {
  Wallet,
  Upload,
  Calendar,
  Settings as SettingsIcon,
  ClipboardCheck,
  BarChart3,
  Printer,
  Download,
  Sun,
  Moon,
  Menu,
  X,
  LogOut,
  User as UserIcon,
  ChevronDown,
  type LucideIcon,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useAuth } from '@/context/AppContext';
import { useTheme, useToast } from '@/hooks/useToast';
import { signOut } from '@/firebase/auth';
import { usePayroll } from '@/context/AppContext';
import { formatDateShort } from '@/lib/payroll';

type NavItem = {
  id: string;
  path: string;
  label: string;
  short: string;
  icon: LucideIcon;
  color: string;
  badge?: string;
};

const NAV: NavItem[] = [
  { id: 'upload', path: '/upload', label: 'رفع الملفات', short: 'رفع', icon: Upload, color: 'from-indigo-500 to-violet-500' },
  { id: 'period', path: '/period', label: 'الفترة', short: 'فترة', icon: Calendar, color: 'from-emerald-500 to-teal-500' },
  { id: 'settings', path: '/settings', label: 'الإعدادات', short: 'إعدادات', icon: SettingsIcon, color: 'from-amber-500 to-orange-500' },
  { id: 'review', path: '/review', label: 'المراجعة والبصمات', short: 'مراجعة', icon: ClipboardCheck, color: 'from-sky-500 to-blue-500', badge: 'موحّد' },
  { id: 'results', path: '/results', label: 'النتائج', short: 'نتائج', icon: BarChart3, color: 'from-violet-500 to-purple-500' },
  { id: 'print', path: '/print', label: 'الطباعة', short: 'طباعة', icon: Printer, color: 'from-slate-700 to-slate-900' },
  { id: 'export', path: '/export', label: 'التصدير', short: 'تصدير', icon: Download, color: 'from-fuchsia-500 to-pink-500' },
];

export function AppLayout({ children }: { children: ReactNode }) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [userMenu, setUserMenu] = useState(false);
  const { user } = useAuth();
  const { period, payrollData, companyName } = usePayroll();
  const { theme, setTheme } = useTheme();
  const { show } = useToast();
  const location = useLocation();

  const handleSignOut = async () => {
    if (!confirm('تسجيل الخروج؟')) return;
    try {
      await signOut();
      show('تم تسجيل الخروج', 'info');
    } catch (e: any) {
      show('حصل خطأ: ' + e.message, 'error');
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/40 backdrop-blur-sm z-30 animate-fade-in"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={cn(
          'fixed lg:sticky top-0 right-0 h-screen w-72 z-40 shrink-0 flex flex-col bg-white/90 dark:bg-slate-900/90 backdrop-blur-xl border-l border-slate-200/70 dark:border-slate-800 shadow-xl transition-transform duration-300',
          sidebarOpen ? 'translate-x-0' : 'translate-x-full lg:translate-x-0',
        )}
      >
        {/* Logo */}
        <div className="p-5 border-b border-slate-200/60 dark:border-slate-800">
          <div className="flex items-center gap-3">
            <div className="relative w-11 h-11 rounded-2xl gradient-primary flex items-center justify-center shadow-lg shadow-indigo-500/30 shrink-0 animate-float">
              <Wallet size={22} className="text-white" />
              <div className="absolute -top-1 -right-1 w-3 h-3 rounded-full bg-emerald-400 border-2 border-white dark:border-slate-900 animate-pulse-soft" />
            </div>
            <div className="min-w-0">
              <h1 className="text-base font-extrabold gradient-text truncate">نظام المرتبات</h1>
              <p className="text-[11px] text-slate-500 dark:text-slate-400 flex items-center gap-1.5">
                <span className="status-dot bg-emerald-500" />
                الإصدار 2.0
              </p>
            </div>
            <button
              onClick={() => setSidebarOpen(false)}
              className="lg:hidden mr-auto p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800"
              aria-label="إغلاق"
            >
              <X size={18} />
            </button>
          </div>
        </div>

        {/* Period summary card */}
        <div className="p-4">
          <div className="rounded-xl p-3 bg-gradient-to-br from-indigo-50 to-violet-50 dark:from-indigo-900/30 dark:to-violet-900/30 border border-indigo-100 dark:border-indigo-800/40">
            <div className="text-[11px] font-semibold text-slate-500 dark:text-slate-400 mb-1">الفترة الحالية</div>
            {payrollData ? (
              <>
                <div className="text-sm font-bold text-indigo-700 dark:text-indigo-300 truncate">
                  {formatDateShort(period.start)} → {formatDateShort(period.end)}
                </div>
                <div className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5">
                  {payrollData.length} موظف
                </div>
              </>
            ) : (
              <div className="text-xs text-slate-400">لم يتم احتساب بعد</div>
            )}
          </div>
        </div>

        {/* Nav */}
        <nav className="flex-1 px-3 overflow-y-auto">
          <div className="text-[11px] font-bold text-slate-400 dark:text-slate-500 px-3 mb-2 tracking-wider uppercase">
            القائمة الرئيسية
          </div>
          <ul className="space-y-1">
            {NAV.map((item) => {
              const active = location.pathname === item.path;
              const Icon = item.icon;
              return (
                <li key={item.id}>
                  <NavLink
                    to={item.path}
                    onClick={() => setSidebarOpen(false)}
                    className={cn('nav-item', active && 'nav-item-active')}
                  >
                    <span
                      className={cn(
                        'w-8 h-8 rounded-lg flex items-center justify-center transition-all shrink-0',
                        active
                          ? 'bg-white/20'
                          : `bg-gradient-to-br ${item.color} text-white shadow-sm`,
                      )}
                    >
                      <Icon size={16} />
                    </span>
                    <span className="truncate">{item.label}</span>
                    {active && <span className="mr-auto w-1.5 h-1.5 rounded-full bg-white animate-pulse-soft" />}
                  </NavLink>
                </li>
              );
            })}
          </ul>
        </nav>

        {/* User card */}
        <div className="p-3 border-t border-slate-200/60 dark:border-slate-800">
          <div className="relative">
            <button
              onClick={() => setUserMenu((v) => !v)}
              className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
            >
              <div className="w-9 h-9 rounded-lg gradient-primary flex items-center justify-center text-white text-sm font-bold shrink-0">
                {(user?.displayName || user?.email || '?').charAt(0).toUpperCase()}
              </div>
              <div className="min-w-0 flex-1 text-right">
                <div className="text-xs font-bold truncate text-slate-800 dark:text-slate-100">
                  {user?.displayName || user?.email?.split('@')[0]}
                </div>
                <div className="text-[11px] text-slate-500 truncate" dir="ltr">
                  {user?.email}
                </div>
              </div>
              <ChevronDown size={14} className="text-slate-400" />
            </button>
            {userMenu && (
              <div className="absolute bottom-full right-0 mb-2 w-full glass rounded-xl shadow-xl overflow-hidden animate-slide-down border border-slate-200 dark:border-slate-700 z-10">
                <div className="p-3 border-b border-slate-200/60 dark:border-slate-700">
                  <div className="text-[10px] text-slate-500 mb-1">مسجّل دخول باسم</div>
                  <div className="text-xs font-bold truncate">{companyName}</div>
                </div>
                <button
                  onClick={handleSignOut}
                  className="w-full px-3 py-2.5 text-right flex items-center gap-2 hover:bg-rose-50 dark:hover:bg-rose-900/20 text-rose-600 dark:text-rose-400 font-semibold text-sm transition-colors"
                >
                  <LogOut size={14} />
                  تسجيل خروج
                </button>
              </div>
            )}
          </div>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 min-w-0 flex flex-col">
        {/* Top bar */}
        <header className="sticky top-0 z-20 glass border-b border-slate-200/60 dark:border-slate-800 no-print">
          <div className="px-4 sm:px-6 py-3 flex items-center gap-3">
            <button
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden w-10 h-10 rounded-xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 flex items-center justify-center hover:scale-105 active:scale-95 transition-all"
              aria-label="فتح القائمة"
            >
              <Menu size={18} />
            </button>
            <div className="flex-1 min-w-0">
              <div className="text-xs text-slate-500 dark:text-slate-400">مرحباً بك في</div>
              <div className="text-sm font-bold truncate text-slate-800 dark:text-slate-100">
                {NAV.find((n) => n.path === location.pathname)?.label || 'الرئيسية'}
              </div>
            </div>
            <button
              onClick={() => setTheme(theme === 'light' ? 'dark' : 'light')}
              className="w-10 h-10 rounded-xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 flex items-center justify-center hover:scale-110 active:scale-95 transition-all"
              title="تبديل الوضع"
            >
              {theme === 'light' ? (
                <Moon size={18} className="text-amber-500" />
              ) : (
                <Sun size={18} className="text-amber-400" />
              )}
            </button>
            <div className="hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700">
              <UserIcon size={14} className="text-slate-400" />
              <span className="text-xs font-semibold truncate max-w-[140px]">
                {user?.displayName || user?.email?.split('@')[0]}
              </span>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 p-4 sm:p-6 lg:p-8 no-print animate-fade-in">
          <div className="max-w-7xl mx-auto">{children}</div>
        </main>
      </div>
    </div>
  );
}
